import React, { useEffect, useState } from 'react'
import { EcomDataLayerHub } from '../../javascript/analytics/ecommerceDataLayerEventHub.services'
import {
  FetchEventDetailsOptions,
  IDateTimeEvent,
  IEpiProps,
  IEventRegistrationRefreshModel,
  IUserSelections,
  StepConstants,
} from '../models/event-reg-refresh-models'
import { useEventRegRefreshContext, useEventRegRefreshDispatch } from '../reducers/event-reg-refresh-reducers'
import { normalizeStateInput, matchUserProgramToSpecialtyProgram } from '../helpers/helpers'
import { DateTimeStep } from './date-time-step'
import { ProgramSummary } from './program-summary'

interface IContextProps {
  epiProps: IEpiProps
  currentStep: string
  userSelections: IUserSelections
  eventRegistrationRefreshModel: IEventRegistrationRefreshModel
  showOnlySharedProgramDateTimeSelection: boolean
  sharedProgramDateTimeEvent?: IDateTimeEvent | null
}

export const DateAndTimeStepSpecialtyProgram = () => {
  const {
    currentStep,
    userSelections,
    epiProps,
    eventRegistrationRefreshModel,
    showOnlySharedProgramDateTimeSelection,
    sharedProgramDateTimeEvent,
  }: IContextProps = useEventRegRefreshContext()!
  const [userCoordinates, setUserCoordinates] = useState<{ latitude: string; longitude: string } | null>(null)

  const dispatch = useEventRegRefreshDispatch()!

  // Initialize filter state from userSelections. Combined locationInput from older share
  // links has already been split into cityState/postalCode by splitLocationInput().
  const [cityStateInput, setCityStateInput] = useState(userSelections.filters?.cityState || '')
  const [postalCodeInput, setPostalCodeInput] = useState(userSelections.filters?.postalCode || '')
  const [rangeInput, setRangeInput] = useState(userSelections.filters?.rangeInput || '')
  const [selectedDates, setSelectedDates] = useState<string[]>(userSelections.filters?.selectedDates || [])
  const [sortByDate, setSortByDate] = useState(userSelections.filters?.sortByDate ?? true)

  // Function to get user's current location coordinates
  const getCurrentCoordinates = () => {
    if (!navigator.geolocation) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Geolocation is not supported by this browser.')
      }
      return
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        if (process.env.NODE_ENV === 'development') {
          console.log(`User coordinates: ${latitude}, ${longitude}`)
        }
        setUserCoordinates({
          latitude: latitude.toString(),
          longitude: longitude.toString()
        })
      },
      (error) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('Error getting location:', error.message)
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000, // 5 minutes cache
      }
    )
  }

  // Get user coordinates on component mount
  useEffect(() => {
    if (eventRegistrationRefreshModel.distanceUnit) {
      getCurrentCoordinates()
    }
  }, [eventRegistrationRefreshModel.distanceUnit])
  
  useEffect(() => {
    if (currentStep === StepConstants.DateTimeSpecialtyProgramStep) {
      EcomDataLayerHub.viewDateTime(userSelections.userProgramSelection.programName)
    }
  }, [
    currentStep,
    userSelections.userProgramSelection?.programName,
    epiProps.selectDateAndTimeStepSpecialtyProgramTitle,
  ]) // Include `currentStep` and `programName` as dependencies to re-fire if the step or program changes.
  
  // Update userSelections when filters change
  useEffect(() => {
    dispatch({
      type: 'updateUserSelections',
      data: {
        ...userSelections,
        filters: {
          cityState: cityStateInput,
          postalCode: postalCodeInput,
          rangeInput,
          selectedDates,
          sortByDate
        }
      }
    })
  }, [cityStateInput, postalCodeInput, rangeInput, selectedDates, sortByDate])

  // Function to fetch event details
  const fetchSpecialtyEventDetails = async (options?: FetchEventDetailsOptions) => {
    const {
      locationFilter: rawLocationFilter = '',
      postalCodeFilter = '',
      rangeFilter = '',
      datesFilter = [],
      sortBy = 'date'
    } = options || {}
    const locationFilter = normalizeStateInput(rawLocationFilter)
    const trimmedPostalCode = postalCodeFilter.trim()
    //// TEST DATA: TEMPORARY: Return mock data for VIC program in en-GB culture
    //if (userSelections?.userProgramSelection?.programCode === 'VIC' &&
    //    window.location.href.includes('en-GB')) {
    //  console.log('Returning test data for VIC program in en-GB culture')
    //  const testData: IDateTimeEvent[] = [
    //    {
    //      culture: "en-GB",
    //      eventDates: "March 15-16, 2025",
    //      eventNumber: "VIC001-TEST",
    //      facility: "London Training Centre",
    //      address1: "123 Oxford Street",
    //      address2: "Suite 500",
    //      city: "London",
    //      state: "England",
    //      country: "United Kingdom",
    //      zip: "W1D 1AN",
    //      timeZone: "GMT",
    //      spokenLanguage: "English",
    //      seatSku: "VIC-SEAT-001",
    //      qualification: "VIC",
    //      isVirtual: false,
    //      isBlended: true,
    //      isFull: false,
    //      isFuture: true,
    //      days: ["Jul 22, 2025 (8:30 AM - 4:30 PM)", "Jul 23, 2025 (8:30 AM - 3:30 PM)", "Jul 24, 2025 (8:30 AM - 3:30 PM)"],
    //      price: 895,
    //      startDate: "2025-03-15T09:00:00Z",
    //      endDate: "2025-03-16T17:00:00Z"
    //    },
    //    {
    //      culture: "en-GB",
    //      eventDates: "April 20-21, 2025",
    //      eventNumber: "VIC002-TEST", 
    //      facility: "Manchester Conference Centre",
    //      address1: "456 Deansgate",
    //      address2: "",
    //      city: "Manchester",
    //      state: "England", 
    //      country: "United Kingdom",
    //      zip: "M3 2JL",
    //      timeZone: "GMT",
    //      spokenLanguage: "English",
    //      seatSku: "VIC-SEAT-002",
    //      qualification: "VIC",
    //      isVirtual: false,
    //      isBlended: false,
    //      isFull: false,
    //      isFuture: true,
    //      days: ["Jul 22, 2025 (8:30 AM - 4:30 PM)", "Jul 23, 2025 (8:30 AM - 3:30 PM)", "Jul 24, 2025 (8:30 AM - 3:30 PM)"],
    //      price: 895,
    //      startDate: "2025-04-20T09:00:00Z",
    //      endDate: "2025-04-21T17:00:00Z"
    //    }
    //  ]
    //  return testData
    //}

    // Build query parameters with filters
    const queryParams = new URLSearchParams({
      specialty: userSelections?.userProgramSelection?.programCode || '',
      programtype:
        userSelections?.userProgramSelection?.isRenewal === true
          ? 'Renewal'
          : userSelections?.userProgramSelection?.isDirectDelivery
            ? 'DirectDelivery'
            : 'ICP',
      qualification: userSelections?.userAddOnProgramSelections?.find(a => !a.disableDateTimeSelection)?.programCode ?? '',
      sortby: sortBy,
      dates: JSON.stringify(datesFilter.length > 0 ? datesFilter : []),
      conference: 'false',
    })

    if (eventRegistrationRefreshModel.distanceUnit) {
      queryParams.set('range', rangeFilter || '150')
      queryParams.set('unit', eventRegistrationRefreshModel.distanceUnit)
    }

    if (eventRegistrationRefreshModel.distanceUnit && locationFilter) {
      queryParams.set('location', locationFilter)
    }

    if (eventRegistrationRefreshModel.distanceUnit && trimmedPostalCode) {
      queryParams.set('postalCode', trimmedPostalCode)
    }

    // Browser geolocation only when both city/state and zip are empty.
    if (eventRegistrationRefreshModel.distanceUnit && !locationFilter && !trimmedPostalCode && userCoordinates) {
      queryParams.set('latitude', userCoordinates.latitude)
      queryParams.set('longitude', userCoordinates.longitude)
    }

    // Construct the URL with query parameters
    const url = `/internalapi/EventRegistrationRefreshApi/EventDetailsList?${queryParams.toString()}`

    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      }
    })

    if (!response.ok) {
      throw new Error(`API call failed with status: ${response.status}`)
    }

    const data = await response.json()
    if (process.env.NODE_ENV === 'development') {
      console.log('Event details loaded:', Array.isArray(data) ? data.length : data, url)
    }
    return Array.isArray(data) ? data : []
  }

  // Handle event selection - toggle between select and deselect
  const handleSelectEvent = (event: IDateTimeEvent) => {
    // Check if this event is already selected
    const isCurrentlySelected = userSelections.userProgramDateTimeSelection?.eventNumber === event.eventNumber

    // Update the global state - if already selected, deselect (set to null), otherwise select the event
    dispatch({
      type: 'updateUserSelections',
      data: {
        ...userSelections,
        userProgramDateTimeSelection: isCurrentlySelected ? null : event,
        userAddOnProgramDateTimeSelection: null, // Clear add-on date/time selection when changing date/time selection
        attendees: [] // Clear attendees when changing date/time selection
      }
    })

    // Clear shared add-on date/time event when specialty program date/time changes
    dispatch({
      type: 'clearSharedAddOnProgramDateTimeEvent'
    })

    // Reset steps after this step regardless of select/deselect
    dispatch({
      type: 'resetStepsAfter',
      data: StepConstants.DateTimeSpecialtyProgramStep
    })
  }

  // Check if an event is currently selected
  const isEventSelected = (eventNumber: string): boolean => {
    return userSelections.userProgramDateTimeSelection?.eventNumber === eventNumber
  }

  // Check if any event is selected for validation
  const isEventSelectedForValidation = (): boolean => {
    return !!userSelections.userProgramDateTimeSelection
  }

  // Check if we should show only shared selection
  const shouldShowOnlyShared = (): boolean => {
    return showOnlySharedProgramDateTimeSelection;
  };

  // Called if user clicks 'show all programs' CTA in the shared selection view (where only shared card is visible)
  const handleShowAllPrograms = () => {
    dispatch({ type: 'setShowOnlySharedProgramDateTimeSelection', data: false });
  };

  // Handle continue button click
  const handleContinue = () => {
    // Check if an event is selected
    if (userSelections.userProgramDateTimeSelection) {
      const addOnSelections = userSelections.userAddOnProgramSelections || []
      const hasAddOns = addOnSelections.length > 0
      const skipAttendeesStep = !eventRegistrationRefreshModel.showPrice

      if (hasAddOns) {
        EcomDataLayerHub.selectSession(
          userSelections.userProgramDateTimeSelection,
          userSelections.userProgramDateTimeSelection.isBlended.toString(),
        )

        const anyAddOnNeedsDateTime = addOnSelections.some(a => !a.disableDateTimeSelection && !a.programSku)

        if (anyAddOnNeedsDateTime) {
          dispatch({ type: 'updateCurrentStep', data: StepConstants.DateTimeAddOnProgramStep })
        } else if (skipAttendeesStep) {
          dispatch({ type: 'updateCurrentStep', data: StepConstants.ReviewStep })
        } else {
          dispatch({ type: 'updateCurrentStep', data: StepConstants.AttendeesStep })
        }
      } else if (skipAttendeesStep) {
        dispatch({ type: 'updateCurrentStep', data: StepConstants.ReviewStep })
      } else {
        dispatch({ type: 'updateCurrentStep', data: StepConstants.AttendeesStep })
      }
    }
  }

  if (currentStep == StepConstants.DateTimeSpecialtyProgramStep) {
    const selectedSpecialtyProgram = matchUserProgramToSpecialtyProgram(
      userSelections.userProgramSelection,
      eventRegistrationRefreshModel
    )

    return (
      <DateTimeStep
        fetchEventDetails={fetchSpecialtyEventDetails}
        handleEventSelection={handleSelectEvent}
        handleContinue={handleContinue}
        isEventSelected={isEventSelected}
        isEventSelectedForValidation={isEventSelectedForValidation}
        title={epiProps.selectDateAndTimeStepSpecialtyProgramTitle}
        description={epiProps.selectDateAndTimeStepSpecialtyProgramDescription}
        validationMessage={epiProps.selectDateAndTimeStepSpecialtyProgramValidationMessage}
        currentStep={currentStep}
        targetStep={StepConstants.DateTimeSpecialtyProgramStep}
        programName={userSelections.userProgramSelection?.programName}
        dateTimeStepIntroMessage={selectedSpecialtyProgram?.dateTimeStepIntroMessage}
        shouldShowOnlyShared={shouldShowOnlyShared()}
        handleShowAllPrograms={handleShowAllPrograms}
        sharedDateTimeEvent={sharedProgramDateTimeEvent}
        cityStateInput={cityStateInput}
        postalCodeInput={postalCodeInput}
        rangeInput={rangeInput}
        selectedDates={selectedDates}
        sortByDate={sortByDate}
        setCityStateInput={setCityStateInput}
        setPostalCodeInput={setPostalCodeInput}
        setRangeInput={setRangeInput}
        setSelectedDates={setSelectedDates}
        setSortByDate={setSortByDate}
      />
    )
  }

  return null
}
